import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../services/data.service';

@Component({
  selector: 'app-stripe-payment-status',
  standalone: true,
  imports: [CommonModule, IonicModule, RouterModule],
  template: `
    <ion-content [fullscreen]="true">
      <div class="payment-status">
        <div class="payment-status__card">
          <div
            class="payment-status__icon"
            [class.payment-status__icon--success]="isSuccess()"
            [class.payment-status__icon--error]="!isSuccess()"
          >
            {{ isSuccess() ? 'OK' : 'KO' }}
          </div>

          <h1>{{ isSuccess() ? 'Pago completado' : 'Pago cancelado' }}</h1>
          <p>
            {{
              isSuccess()
                ? syncMessage()
                : 'El pago no se ha completado. Puedes volver a intentarlo desde la aplicacion.'
            }}
          </p>

          <ion-spinner *ngIf="isSuccess() && isRefreshing()"></ion-spinner>

          <div class="payment-status__actions">
            <ion-button expand="block" color="primary" (click)="goHome()">
              Ir al inicio
            </ion-button>
            <ion-button
              *ngIf="isSuccess()"
              expand="block"
              fill="outline"
              color="success"
              (click)="goProfile()"
            >
              Ver mi perfil
            </ion-button>
          </div>
        </div>
      </div>
    </ion-content>
  `,
  styles: [`
    .payment-status {
      min-height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
    }

    .payment-status__card {
      width: 100%;
      max-width: 28rem;
      padding: 24px;
      border-radius: 24px;
      background: #ffffff;
      box-shadow: 0 20px 50px rgba(15, 23, 42, 0.12);
      text-align: center;
    }

    .payment-status__icon {
      width: 72px;
      height: 72px;
      margin: 0 auto 16px;
      border-radius: 9999px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      color: #ffffff;
    }

    .payment-status__icon--success {
      background: #16a34a;
    }

    .payment-status__icon--error {
      background: #dc2626;
    }

    .payment-status__card h1 {
      margin: 0 0 12px;
      font-size: 1.5rem;
      font-weight: 700;
      color: #0f172a;
    }

    .payment-status__card p {
      margin: 0 0 20px;
      color: #475569;
      line-height: 1.6;
    }

    .payment-status__actions {
      display: grid;
      gap: 12px;
      margin-top: 20px;
    }
  `],
})
export class StripePaymentStatusComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dataService = inject(DataService);

  readonly isRefreshing = signal(false);
  readonly syncMessage = signal('Estamos validando tu pago y actualizando tus datos...');
  readonly isSuccess = signal(this.route.snapshot.data['status'] === 'success');

  async ngOnInit() {
    if (this.isSuccess()) {
      await this.refreshCreditsAfterPayment();
    }
  }

  async goHome() {
    await this.router.navigate(['/tabs/home']);
  }

  async goProfile() {
    await this.router.navigate(['/tabs/profile']);
  }

  private async refreshCreditsAfterPayment() {
    const user = this.dataService.getUser() ?? this.dataService.getLocalStorage('user');
    if (!user?.id) {
      this.syncMessage.set('Pago recibido. Inicia sesion para ver tus datos actualizados.');
      return;
    }

    this.isRefreshing.set(true);

    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const credits: any = await firstValueFrom(this.dataService.getCredits(user.id, true));
        const settled = Array.isArray(credits)
          ? credits.some((credit: any) => credit?.isOnlyMuscle && (!credit?.stripeCheckoutUrl || !!credit?.stripeSubscriptionId))
          : false;

        if (settled) {
          this.syncMessage.set('Pago confirmado y datos actualizados correctamente.');
          this.isRefreshing.set(false);
          return;
        }
      } catch {}

      await new Promise((resolve) => setTimeout(resolve, 1500));
    }

    this.syncMessage.set('Pago recibido. Si no ves el cambio todavia, vuelve a entrar en unos segundos.');
    this.isRefreshing.set(false);
  }
}
